import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../../api'
import { Icon } from '../Icon'

// BroadcastModal — manager-only one-way message composer (Live View
// PR L4 / spec 33 §"Broadcast", v5 prototype L3605-3639). Sends a
// note to every currently-signed-on staff member via
// POST /api/events/{id}/broadcasts. recipient_count is computed
// server-side at send time so the displayed audience matches what
// the row records.
//
// v1 fans out to "everyone on shift" — per-group scoping (Welcome
// Hosts only / Carriage Stewards / Duty Managers) was prototype
// affordance only and is deferred until we have group definitions.
//
// Toast on send (parent-managed): "Broadcast sent to N on-shift staff"

interface Props {
  eventId: number
  onShiftCount: number
  onClose: () => void
  onSent: (recipientCount: number) => void
}

const QUICK_INSERTS: ReadonlyArray<{ label: string; body: string }> = [
  { label: '+ Service delay',   body: 'Service running 5 minutes late' },
  { label: '+ Lunch reminder',  body: 'Lunch coverage starts in 5 minutes' },
  { label: '+ All-hands',       body: 'All-hands at the Welcome Hut in 10 minutes' }
]

export function BroadcastModal({ eventId, onShiftCount, onClose, onSent }: Props) {
  const [body, setBody] = useState('')
  const qc = useQueryClient()

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [])

  const send = useMutation({
    mutationFn: () => ApiClient.createBroadcast(eventId, { body }),
    onSuccess: (record) => {
      qc.invalidateQueries({ queryKey: ['live-pulse', eventId] })
      qc.invalidateQueries({ queryKey: ['event-broadcasts', eventId] })
      onSent(record.recipientCount)
      onClose()
    }
  })

  const trimmed = body.trim()
  const canSend = trimmed.length > 0 && !send.isPending

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Broadcast to crew"
      className="fixed inset-0 z-[400] flex items-center justify-center"
      style={{ background: 'rgba(0, 0, 0, 0.7)', padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-parchment text-ink rounded-xl w-full"
        style={{ maxWidth: 560, padding: 28, boxShadow: '0 24px 60px -10px rgba(0,0,0,0.45)' }}
      >
        <div className="flex items-center gap-3 mb-3">
          <div
            className="flex items-center justify-center bg-brass-soft"
            style={{ width: 44, height: 44, borderRadius: '50%', color: '#9b7f2a', flexShrink: 0 }}
          >
            <Icon name="message-square" size={22} />
          </div>
          <h3 className="font-display" style={{ fontSize: 22, fontWeight: 600 }}>
            Broadcast to Crew
          </h3>
        </div>

        <p className="text-sm text-ink-soft mb-4">
          Sends a note to every volunteer currently signed on. Use sparingly —
          they're on shift.
        </p>

        <label
          className="block mb-1"
          style={{
            fontSize: 11.5,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 700,
            color: '#5a5a5a'
          }}
        >
          Send to
        </label>
        <div
          className="mb-4 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-line text-sm"
          aria-live="polite"
        >
          <span className="font-semibold">Everyone on shift</span>
          <span className="text-ink-soft">·</span>
          <span className="font-mono text-ink-soft">{onShiftCount} signed on</span>
        </div>

        <label
          className="block mb-1"
          style={{
            fontSize: 11.5,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 700,
            color: '#5a5a5a'
          }}
        >
          Message
        </label>
        <textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="e.g. Service 5 running 5 minutes late — please update visitors at platform entrance."
          disabled={send.isPending}
          className="w-full rounded-md border border-line bg-white text-ink p-3 text-sm"
          style={{ resize: 'vertical', fontFamily: 'inherit' }}
          maxLength={2000}
        />
        <div className="flex gap-2 mt-2 flex-wrap">
          {QUICK_INSERTS.map((q) => (
            <button
              key={q.label}
              type="button"
              onClick={() => setBody(q.body)}
              disabled={send.isPending}
              className="px-3 py-1.5 rounded text-xs font-semibold border border-line text-ink-soft hover:text-ink hover:border-ink disabled:opacity-50"
            >
              {q.label}
            </button>
          ))}
        </div>

        {send.isError && (
          <div className="mt-3 bg-oxblood-soft border border-oxblood text-oxblood rounded p-2 text-xs">
            Could not send broadcast: {(send.error as Error).message}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={send.isPending}
            className="px-4 py-2 rounded text-sm text-ink-soft hover:bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => send.mutate()}
            disabled={!canSend}
            className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-semibold bg-brass text-white hover:bg-brass/90 disabled:opacity-50"
          >
            <Icon name="message-square" size={14} />
            {send.isPending ? 'Sending…' : `Send to ${onShiftCount} crew`}
          </button>
        </div>
      </div>
    </div>
  )
}
